import { useQuery, useMutation } from '@tanstack/react-query'
import { NotificationsFilterParams, SetReadNotificationParams } from '@/types/notification'
import { BulkDeleteResponse } from '@/types/general'
import { useNotificationsStore } from '@/store'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'

export const useNotifications = (params: NotificationsFilterParams, options?: { enabled?: boolean }) => {
  const fetchNotifications = useNotificationsStore(state => state.fetchNotifications)

  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true
  })
}

export const useSetReadNotifications = () => {
  const setReadNotifications = useNotificationsStore(state => state.setReadNotifications)

  return useMutation({
    mutationFn: ({ params }: { params: SetReadNotificationParams }) => setReadNotifications(params),
    onSuccess: () => {}
  })
}

export const useDeleteNotification = () => {
  const deleteNotification = useNotificationsStore(state => state.deleteNotification)

  return useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteNotifications = () => {
  const t = useTranslations()
  const bulkDeleteNotifications = useNotificationsStore(state => state.bulkDeleteNotifications)
  const deselectAllNotifications = useNotificationsStore(state => state.deselectAllNotifications)

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteNotifications(ids),
    onSuccess: (response: BulkDeleteResponse) => {
      // Clear selection after successful deletion
      deselectAllNotifications()

      const successCount = response.deletedItems.length
      const failCount = Object.keys(response.failedItems).length

      if (failCount === 0) {
        // All items were deleted successfully
        toast.success(t('bulk_delete_success', { count: successCount }))
      } else if (successCount > 0) {
        // Some items were deleted, but some failed
        toast.success(t('bulk_delete_partial_success', { successCount, failCount }))

        // Show error details for failed items
        Object.entries(response.failedItems).forEach(([id, errorMessage]) => {
          toast.error(`${id}: ${errorMessage}`)
        })
      } else {
        // All items failed to delete
        toast.error(t('bulk_delete_failed_items'))

        // Show error details for failed items
        Object.entries(response.failedItems).forEach(([id, errorMessage]) => {
          toast.error(`${id}: ${errorMessage}`)
        })
      }
    }
  })
}
